import React, { FC } from 'react';
import { Compare } from '@navi-toolbox/ui-components';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Product } from 'common/components/product-card/types';
import { getProductsCompareListLimit, getProductsCompareListsLimit } from 'common/selectors';
import { analyticsUtils } from 'utils/analytics-utils';
import { getIsInComparison, getProductsCompareLists } from '../../ducks/selectors';
import {
  MANAGE_PRODUCT_IN_COMPARE_LIST,
  RESET_VALIDATION,
  SEND_PRODUCTS_COMPARE_ACTION,
  SET_VALIDATION_ERROR,
} from '../../ducks/actions';
import { CompareActions } from '../../ducks/types';
import { getAnalyticsData } from './utils';

export const DATA_QA_COMPARE_LIST_ACTION = 'compare-list-action';

type CompareListActionProps = {
  hideText?: boolean;
  product: Product;
  productPosition: number;
  elementBlock: string;
};

export const CompareListAction: FC<CompareListActionProps> = ({
  hideText = undefined,
  product,
  productPosition,
  elementBlock,
}) => {
  const dispatch = useDispatch();
  const { productId, compareCategory, displayedName: productName } = product;
  const groupName = compareCategory?.name ?? '';
  const familyId = compareCategory?.familyId ?? '';
  const isInComparison = useSelector(getIsInComparison(familyId ?? '', productId));
  const compareLists = useSelector(getProductsCompareLists);
  const compareListLimit = useSelector(getProductsCompareListLimit);
  const compareListsLimit = useSelector(getProductsCompareListsLimit);

  const handleRemoveCompareList = () => {
    const currentGroup = compareLists?.find((list) => list.groupId === familyId);
    const isLastProduct = currentGroup?.products?.length === 1;

    if (isLastProduct) {
      dispatch(
        MANAGE_PRODUCT_IN_COMPARE_LIST({
          action: CompareActions.DELETE_GROUP,
          productId,
          familyId,
          groupName,
          withData: false,
        }),
      );

      dispatch(
        SEND_PRODUCTS_COMPARE_ACTION({
          action: CompareActions.DELETE,
          productId,
          familyId,
          groupId: familyId,
          groupName,
        }),
      );
    } else {
      dispatch(
        SEND_PRODUCTS_COMPARE_ACTION({
          action: CompareActions.DELETE,
          productId,
          familyId,
          groupId: familyId,
          groupName,
        }),
      );

      dispatch(
        MANAGE_PRODUCT_IN_COMPARE_LIST({
          action: CompareActions.DELETE,
          productId,
          familyId,
          groupName,
          withData: false,
        }),
      );
    }
  };

  const handleAddCompareList = () => {
    dispatch(
      SEND_PRODUCTS_COMPARE_ACTION({
        action: CompareActions.ADD,
        productId,
        familyId,
        groupId: familyId,
        groupName,
        withData: false,
      }),
    );

    dispatch(
      MANAGE_PRODUCT_IN_COMPARE_LIST({
        action: CompareActions.ADD,
        productId,
        familyId,
        groupName,
        withData: false,
      }),
    );
  };

  const handleCompareList = () => {
    const analyticsDataByElementBlock = getAnalyticsData(elementBlock, product);

    analyticsUtils.sendEvent(isInComparison ? 'GA:removeFromComparisonList' : 'GA:addToComparisonList', {
      elementType: 'button',
      elementBlock: 'productList / Catalog',
      elementName: 'comparisonList',
      productName,
      productId,
      productPrice: product.price.main_price,
      productPrevPrice: product.price.previous_price ? product.price.previous_price : null,
      productPosition,
      ...analyticsDataByElementBlock,
    });

    if (isInComparison) {
      handleRemoveCompareList();
      dispatch(RESET_VALIDATION());
    } else {
      const currentGroup = compareLists?.find((list) => list.groupId === familyId);
      const isProductListCountInvalid = currentGroup && currentGroup.products.length >= compareListLimit;
      const isListsCountInvalid = !currentGroup && compareLists.length >= compareListsLimit;
      if (!compareLists.length || !(isProductListCountInvalid || isListsCountInvalid)) {
        handleAddCompareList();
      } else {
        if (isListsCountInvalid) {
          dispatch(SET_VALIDATION_ERROR('listsCount'));
        }
        if (isProductListCountInvalid) {
          dispatch(SET_VALIDATION_ERROR('productListCount'));
        }
      }
    }
  };

  const { t } = useTranslation();

  const addedButtonText = t('ui.compare.list-action-added');
  const notAddedButtonText = t('ui.compare.list-action-not-added');

  const buttonTextByActive = isInComparison ? addedButtonText : notAddedButtonText;
  const buttonText = hideText ? undefined : buttonTextByActive;
  const iconSize = hideText ? 'l' : 'm';

  return (
    <Compare
      data-qa={DATA_QA_COMPARE_LIST_ACTION}
      onClick={handleCompareList}
      active={isInComparison}
      buttonText={buttonText}
      size={iconSize}
    />
  );
};
